<template>
  <div class="calendar">
    <header class="calendar-header">
      <button @click="prevMonth" class="nav-button prev-button">
        <i class="fas fa-chevron-left"></i>
      </button>
      <div>
        <select v-model="selectedYear" @change="updateCalendar">
          <option v-for="year in yearRange" :key="year" :value="year">
            {{ year }}
          </option>
        </select>
        <select v-model="selectedMonth" @change="updateCalendar">
          <option v-for="(month, index) in months" :key="month" :value="index">
            {{ month }}
          </option>
        </select>
        <select v-model="selectedView" @change="updateCalendar">
          <option value="events">Events</option>
          <option value="posts">News</option>
        </select>
      </div>
      <button @click="nextMonth" class="nav-button next-button">
        <i class="fas fa-chevron-right"></i>
      </button>
    </header>

    <div class="calendar-grid">
      <div class="day-name" v-for="day in dayNames" :key="day">{{ day }}</div>
      <div
        class="day-cell"
        v-for="(day, index) in calendarDays"
        :key="index"
        :class="getDayCellClasses(day)"
        @click="getItemsForDay(day)"
      >
        <span>{{ day.date.getDate() }}</span>
      </div>
    </div>

    <div v-if="selectedView == 'posts' && hasNews" class="newsfeed">
      <team-newsfeed
        :key="after.toISOString()"
        :team-id="teamId"
        :is-user-logged-in="isUserLoggedIn"
        :before="before"
        :after="after"
      />
    </div>
    <div v-if="selectedView == 'events' && hasEvents" class="newsfeed">
      <team-events
          :key="after.toISOString()"
          :team-id="teamId"
          :activityId="null"
          :event-array="eventsForDate"
          :is-admin="false"
          :is-user-logged-in="isUserLoggedIn"
        />
    </div>
  </div>
</template>


  <script>
  import { getTeamNews } from '../../api/team';
  import TeamNewsfeed from './TeamNewsfeed.vue';
  import TeamEvents from './TeamEvents.vue';

  export default {
    name: 'TeamCalendar',
    components: {
      TeamNewsfeed,
      TeamEvents,
    },
    props: {
      teamId: {
        type: Number,
        required: true,
      },
      isUserLoggedIn: {
        type: Boolean,
        required: false,
      },
      eventArray: {
        type: Array,
        required: true,
      }
    },
    data() {
      return {
        selectedYear: new Date().getFullYear(),
        selectedMonth: new Date().getMonth(),
        calendarDays: [],
        months: [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"
        ],
        dayNames: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        newsMap: new Map(),
        eventsMap: new Map(),
        selectedView: 'posts',
        hasNews: false,
        hasEvents: false,
        before: null,
        after: null,
      };
    },
    computed: {
      yearRange() {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
      },
      eventsForDate() {
        const selectedDate = this.after.toDateString();
        return this.eventsMap.get(selectedDate) || [];
      },
    },
    methods: {
      async generateCalendar() {
        this.after = new Date();
        this.before = new Date();

        const firstDayOfMonth = new Date(this.selectedYear, this.selectedMonth, 1);
        const lastDayOfMonth = new Date(this.selectedYear, this.selectedMonth + 1, 0);

        const startDate = new Date(firstDayOfMonth);
        startDate.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());

        const endDate = new Date(lastDayOfMonth);
        endDate.setDate(lastDayOfMonth.getDate() + (6 - lastDayOfMonth.getDay()));

        const { data, success } = await getTeamNews({
          teamId: this.teamId,
          before: new Date(this.selectedYear, this.selectedMonth + 1, 1).toISOString(),
          after: firstDayOfMonth.toISOString(),
        });

        if (success) {
          this.newsMap = new Map(
            data.items.map(news => [new Date(news.createdOn).toDateString(), true])
          );
        }

        for (let i = 0; i < this.eventArray.length; i++) {
          const event = this.eventArray[i];
          const eventDate = new Date(event.startDate);
          const eventDateString = eventDate.toDateString();

          // Add the event to the startDate
          if (this.eventsMap.has(eventDateString)) {
            let events = this.eventsMap.get(eventDateString);
            let eventExists = false;
            for( let i=0; i<events.length; i++) {
              if( events[i].id == event.id) {
                eventExists = true;
                break;
              }
            }
            if( !eventExists )
              this.eventsMap.get(eventDateString).push(event);
          } else {
            this.eventsMap.set(eventDateString, [event]);
          }

          // If there is an endDate, include all dates in the range
          if (event.endDate != null) {
            const endDate = new Date(event.endDate);

            for (
              let date = new Date(eventDate);
              date <= endDate;
              date.setDate(date.getDate() + 1)
            ) {
              const currentEventDateString = date.toDateString();

              if (this.eventsMap.has(currentEventDateString)) {
                let events = this.eventsMap.get(currentEventDateString);
                let eventExists = false;
                for( let i=0; i<events.length; i++) {
                  if( events[i].id == event.id) {
                    eventExists = true;
                    break;
                  }
                }
                if( !eventExists )
                  this.eventsMap.get(currentEventDateString).push(event);
              } else {
                this.eventsMap.set(currentEventDateString, [event]);
              }
            }
          }
        }


        const days = [];
        for (
          let date = new Date(startDate);
          date <= endDate;
          date.setDate(date.getDate() + 1)
        ) {
          days.push({
            date: new Date(date),
            isCurrentMonth: date.getMonth() === this.selectedMonth,
          });
        }

        this.calendarDays = days;
      },
      prevMonth() {
        if (this.selectedMonth === 0) {
          this.selectedMonth = 11;
          this.selectedYear -= 1;
        } else {
          this.selectedMonth -= 1;
        }
        this.updateCalendar();
      },
      nextMonth() {
        if (this.selectedMonth === 11) {
          this.selectedMonth = 0;
          this.selectedYear += 1;
        } else {
          this.selectedMonth += 1;
        }
        this.updateCalendar();
      },
      updateCalendar() {
        this.generateCalendar();
      },
      getItemsForDay(day) {
        this.hasNews = false;
        this.hasEvents = false;
        const selectedDate = day.date;
        this.before = new Date(selectedDate);
        this.before.setDate(this.before.getDate() + 1);
        this.after = new Date(selectedDate);
        if( this.selectedView == 'posts') {
          this.hasNews = this.newsMap.get(selectedDate.toDateString()) || false;
        }
        else if( this.selectedView == 'events') {
          this.hasEvents = this.eventsMap.get(selectedDate.toDateString()) || false;
        }
      },

      getDayCellClasses(day) {
        if (!day.isCurrentMonth) return 'not-current-month';
        if( this.selectedView == 'events' && this.eventsMap.get(day.date.toDateString())) return 'has-events';
        if (this.selectedView == 'posts' && this.newsMap.get(day.date.toDateString())) return 'has-events';
        return 'no-events';
      },
    },
    mounted() {
      this.generateCalendar();
    },
  };

  </script>
  
  <style>
  @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css');

  .calendar {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
  }
  .calendar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
  .calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 5px;
    width: 100%;
  }

  .events {
    margin-top: 5px;
  }
  .event-link {
    display: block;
    font-size: 0.9em;
    text-decoration: none;
    color: blue;
  }
  .day-name,
  /* Default style for calendar cells */  
  .day-cell {
    padding: 10px;
    text-align: center;
    border: 1px solid #ddd;
    font-size: 14px;
    cursor: pointer;
  }
  .day-cell.not-current-month {
      color: #ccc;
  }
  /* Style for days with no events */
  .day-cell.no-events {
    background-color: transparent; /* No background */
    color: inherit; /* Use default text color */
  }

  /* Style for days with events */
  .day-cell.has-events {
    background-color: blue; /* Blue background */
    color: white; /* White text */
    font-weight: bold; /* Optional: Makes it more noticeable */
  }

  /* Common hover effect */
  .day-cell:hover {
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
    transform: scale(1.05);
    transition: transform 0.2s, box-shadow 0.2s;
  }

</style>
  